<template>
<div>
    <message-card class="mb-3" state="bad" :message="error_message"></message-card>

    <card v-if="show_tag_tenants_card" size="small" class="mb-3">
        <template slot="body">
            <div class="flex ai-c">
                <div class="flex-a flex jc-sb ai-c">
                    <div class="danger-text">
                        <i class="material-icons">announcement</i>

                        <span v-if="!cycle || !cycle.initial_letter_send_date || !move_out_date" class="ml-1">You have until 2 days before the Audit Letter Date to select any tenants who have moved out.</span>
                        <span v-else class="ml-1">You have until {{ move_out_date }} to select any tenants who have moved out.</span>
                    </div>

                    <div class="ml-3">
                        <button class="small danger" @click="tag_tenants_modal_open = true">Click to Proceed</button>
                    </div>
                </div>
            </div>

            <div v-if="cycle.stats.tagged_for_removal === 0">
                <hr />

                <div class="flex ai-fs jc-sb">
                    <div>
                        <div><strong>You have tagged 0 tenants as moved out.</strong> Please confirm this is correct. If tenants have moved out, please click the button above to mark them now.</div>
                    </div>

                    <button class="small" @click="confirmNoMoveOuts">Confirm</button>
                </div>
            </div>

            <div v-else>
                <hr />

                <div class="flex ai-fs jc-sb">
                    <div>
                        <strong>You have tagged {{ cycle.stats.tagged_for_removal }} tenants as moved out.</strong> When you are finished tagging tenants, please click the button to the right to complete the process.
                    </div>

                    <button class="small" @click="confirmTaggingComplete">Complete</button>
                </div>
            </div>
        </template>
    </card>

    <div class="flex" v-if="cycle">
        <card class="flex-33 mr-3" size="small">
            <template slot="body">
                <label>Planned Audit Letter Date</label>
                <span v-if="cycle.initial_letter_send_date === null" class="gray-message">No date set.</span>
                <datepicker v-else :format="'M/dd/yyyy'" :value="cycle.initial_letter_send_date" disabled />
            </template>
        </card>

        <card class="flex-33 ml-3 mr-3" size="small">
            <template slot="body">
                <label>Planned Reminder Letter Date</label>
                <span v-if="cycle.reminder_letter_send_date === null" class="gray-message">No date set.</span>
                <datepicker v-else :format="'M/dd/yyyy'" :value="cycle.reminder_letter_send_date" disabled />
            </template>
        </card>

        <card class="flex-33 ml-3" size="small">
            <template slot="body">
                <label>Letter Effective Date</label>
                <span v-if="cycle.letter_effective_date === null" class="gray-message">No date set.</span>
                <datepicker v-else :format="'M/dd/yyyy'" :value="cycle.letter_effective_date" disabled />
            </template>
        </card>
    </div>

    <card class="mt-3">
        <template slot="header">
            <div class="flex jc-sb">
                <div class="flex ai-c">
                    <i class="material-icons">people</i>
                    <strong class="ml-2">Tenant List</strong>

                    <span class="hint ml-2 mr-2">|</span>

                    <button
                        :disabled="last_row_count === 0 || is_loading_export"
                        class="icon"
                        title="Export"
                        @click="exportData">
                        <i class="material-icons">save_alt</i>
                    </button>

                    <img v-show="is_loading_export" class="ml-3" src="../../assets/img/loading-black-32.gif" alt="loading" />

                    <span v-show="export_download_path !== null" class="ml-3">
                        <a :href="export_download_path" target="_blank">Download</a>
                        <button class="text ml-6" @click="export_filename = null">
                            <i class="material-icons">clear</i>
                        </button>
                    </span>
                </div>

                <div class="flex-0">
                    <button :disabled="is_loading_tenants" class="icon" type="button" @click="$refs.tenants_table.reload()">
                        <i class="material-icons">refresh</i>
                    </button>
                </div>
            </div>
        </template>

        <template slot="body">
            <div>
                <div>
                    <div class="flex ai-c jc-sb">
                        <div class="flex-0 mr-3 ws-nw">Filter tenants:</div>

                        <select-input
                            :active-index="active_filter_index"
                            :choices="filters"
                            :disabled="is_loading_tenants"
                            @change="(index) => active_filter_index = index"></select-input>
                    </div>

                    <div class="flex ai-c jb-sb mt-5">
                        <div class="flex-1" style="width: 100px;">
                            <div v-if="data_info && data_info.incomplete_address > 0" class="danger-text flex ai-c mt-5">
                                <span class="mr-2"><i class="material-icons">announcement</i></span>

                                <span v-if="data_info.incomplete_address === 1">There is 1 tenant with an incomplete address!</span>
                                <span v-else>There are {{ data_info.incomplete_address.toLocaleString() }} tenants with an incomplete address!</span>
                            </div>
                            <div v-else>&nbsp;</div>
                        </div>

                        <div class="flex-auto">
                            <vue-table-pagination
                                ref="tenants_table_pagination"
                                @vuetable-pagination:change-page="onChangePage">
                            </vue-table-pagination>
                        </div>

                        <div class="flex-1 ta-r" style="width: 100px;">
                        </div>
                    </div>
                </div>

                <div class="mt-5">
                    <div :class="['table-wrapper', 'table-loads', { loading: is_loading_tenants }]">
                        <div class="table-error" v-show="load_tenants_error">
                            <span>{{ load_tenants_error }}</span>
                            <button class="text mt-2" :disabled="is_loading_tenants" @click="$refs.tenants_table.reload()">Retry?</button>
                        </div>

                        <vue-table ref="tenants_table"
                            :load-on-start="false"
                            :api-url="tenants_api_url"
                            :append-params="api_params"
                            data-path="data.data"
                            pagination-path="data.pagination"
                            :per-page="100"
                            table-height="475px"
                            :http-options="http_options"
                            :fields="columns"
                            :data-manager="manageTableFunc"
                            no-data-template="<span>No tenants to show.</span>"
                            @vuetable:pagination-data="onPaginationData"
                            @vuetable:loading="is_loading_tenants = true"
                            @vuetable:loaded="is_loading_tenants = false"
                            @vuetable:load-success="onTableLoadSuccess"
                            @vuetable:load-error="onTableError"
                            @vuetable:row-clicked="onRowClicked">
                            <template slot="checkbox" slot-scope="props">
                                <checkbox
                                    :alone="true"
                                    :center="true"
                                    :checked="checked.indexOf(props.rowData.id) !== -1"
                                    @click="toggleCheckbox(props.rowData.id)"></checkbox>
                            </template>

                            <template slot="name" slot-scope="props">
                                <div class="flex jc-sb ai-c">
                                    <span>{{ props.rowData.last_name }}, {{ props.rowData.first_name }}</span>

                                    <span v-show="props.rowData.welcome_letter_sent_date !== null" title="Locked" class="success-text cu-d">
                                        <i class="material-icons">lock</i>
                                    </span>
                                </div>
                            </template>

                            <template slot="billing_date" slot-scope="props">
                                <div class="pos-r">
                                    <span v-html="formatDateFunc(props.rowData.billing_date)"></span>

                                    <span class="cu-d pos-a r-0 vertically-center success-text"
                                        title="Anniversary billing"
                                        v-if="props.rowData.billing_anniversary == true">
                                        <i class="material-icons">today</i>
                                    </span>
                                </div>
                            </template>

                            <template slot="charge_apply_date" slot-scope="props">
                                <span v-html="formatDateFunc(props.rowData.charge_apply_date)"></span>
                            </template>

                            <template slot="charge_added" slot-scope="props">
                                <span class="success-text" v-if="props.rowData.charge_applied_date !== null"
                                    :title="'Added ' + props.rowData.charge_applied_date">
                                    <i class="material-icons">check_circle</i>
                                </span>
                                <span v-else-if="props.rowData.coverage_id !== null && ((new Date()) >= (new Date(props.rowData.charge_apply_date))) && props.rowData.welcome_letter_sent_date === null">
                                    <button class="small" @click.stop="onConfirmChargedAdded(props.rowData)">Confirm Charge Added</button>
                                </span>

                                <span class="badge secondary cu-h" v-if="props.rowData.setup_insurance_date !== null" title="Tenant was setup via API">
                                    API
                                </span>
                            </template>

                            <template slot="has_insurance" slot-scope="props">
                                <span class="success-text" v-if="props.rowData.coverage_id !== null">
                                    <i class="material-icons">check_circle</i>
                                </span>
                            </template>

                            <template slot="status" slot-scope="props">
                                <div v-if="props.rowData.status === null" class="gray-message">No response</div>

                                <div v-else class="flex ai-c jc-sb">
                                    <div>
                                        <span :class="[props.rowData.status.toLowerCase().replace(' ', '-')]">
                                            {{ props.rowData.status }}
                                        </span>

                                        <strong v-if="props.rowData.tenant_made_selection" class="badge cu-h ml-1" title="Tenant made selection">
                                            T
                                        </strong>
                                    </div>
                                </div>
                            </template>
                        </vue-table>
                    </div>
                </div>
            </div>
        </template>
    </card>

    <managers-edit-tenant-modal
        v-if="edit_tenant_modal_open"
        :tenant="edit_tenant"
        :coverages="coverage_choices"
        :is-loading="is_saving_tenant"
        :error-message="error_saving_tenant_message"
        @updateCoverage="onUpdateCoverage"
        @close="onEditModalClose"
        @save="onSaveTenant"></managers-edit-tenant-modal>

    <managers-tag-tenants-modal
        v-if="tag_tenants_modal_open"
        :cycle="cycle"
        :facility="facility"
        @close="onTagTenantsModalClose"
        @save="onSaveTagTenants"></managers-tag-tenants-modal>
</div>
</template>

<script>
import Vue from "vue"
import http from "@/http"
import store from "@/store"
import { formatDate, cycleAuditColumns } from "@/utilities/columns"
import { objectToParams } from "@/utilities/form-data"

export default {
    name: "manage-tenants-page",

    data () {
        return {
            active_filter_index: 0,
            checked: [],
            columns: cycleAuditColumns,
            coverages: [],
            cycle: null,
            data_info: null,
            edit_tenant_modal_open: false,
            edit_tenant: {},
            error_message: "",
            error_saving_tenant_message: "",
            export_filename: null,
            filters: [],
            formatDateFunc: formatDate.bind(this),
            http_options: {
                baseURL: this.$http.defaults.baseURL,
                headers: {
                    "X-Authorization": store.state.token.token
                },
                transformResponse: [(raw) => {
                    try {
                        return JSON.parse(raw)
                    } catch (error) {
                        this.load_tenants_error = "Unknown error loading tenants."

                        return raw
                    }
                }]
            },
            is_loading_export: false,
            is_loading_tenants: false,
            is_saving_tenant: false,
            last_row_count: 0,
            load_tenants_error: "",
            manageTableFunc: this.manageTable.bind(this),
            sort_field: "",
            tag_tenants_modal_open: false
        }
    },

    mounted () {
        this.loadData()
    },

    computed: {
        api_params () {
            const params = {
                cycle_id: this.cycle_id
            }

            const filter = this.filters[this.active_filter_index]
            if (filter) {
                params.filter = filter.key
            }

            return params
        },

        coverage_choices () {
            const choices = this.coverages.map((coverage) => {
                return {
                    name: coverage.id,
                    label: `$${coverage.premium} per month for $${parseInt(coverage.coverage).toLocaleString()} coverage`
                }
            })

            choices.push({ name: "provide_proof", label: "Provided proof of insurance" })
            choices.push({ name: "opt_out", label: "Opted out" })
            choices.push({ name: "moved_out", label: "Moved Out" })
            choices.push({ name: "bad_address", label: "Bad Address" })

            return choices
        },

        cycle_id () {
            return this.$route.params.cycle_id
        },

        export_download_path () {
            if (this.export_filename === null) {
                return null
            }

            let path = `${this.$http.defaults.baseURL}/api/exports/download`

            path += `?file_key=${this.export_filename}`
            path += "&filename=tenant_list"

            return path
        },

        facility () {
            return store.state.userFacility.facility
        },

        move_out_date () {
            if (!this.cycle || !this.cycle.initial_letter_send_date) {
                return null
            }

            // get the date and normalize the hours to prevent time zone jumping
            const date = new Date(this.cycle.initial_letter_send_date)
            date.setUTCHours(date.getHours())

            // set the day to two days earlier
            date.setDate(date.getDate() - 2)

            // left pad the day value with a 0, if necessary
            let day = date.getDate()
            if (String(day).length < 2) {
                day = `0${day}`
            }

            return `${date.getMonth() + 1}/${day}/${date.getFullYear()}`
        },

        show_tag_tenants_card () {
            if (!this.cycle) {
                return false
            }

            if (!this.cycle.can_tag_removal) {
                return false
            }

            if (!this.cycle.ready_to_tag) {
                return false
            }

            return true
        },

        tenants_api_url () {
            const user = store.state.user

            if (user) {
                return `/api/facilities/${user.facility_id}/tenants`
            } else {
                return null
            }
        }
    },

    watch: {
        active_filter_index () {
            Vue.nextTick(() => {
                this.$refs.tenants_table.reload()
            })
        }
    },

    methods: {
        async loadData () {
            await store.dispatch("userFacility/loadFacility")

            await Promise.all([
                this.getCoverages(),
                this.getTenants(),
                this.getCycle()
            ])

            this.getFilters()

            this.normalizeDates()
        },

        async confirmNoMoveOuts () {
            const confirmed = confirm("Are you sure?")

            if (confirmed) {
                try {
                    const params = new URLSearchParams()
                    params.append("cycle_id", this.cycle.id)

                    await http.post("/api/managers/confirm-move-outs", params)

                    this.$eventManager.emit("add-toast", { message: "Move-outs confirmed!", type: "success" })

                    this.cycle.confirmed_no_move_outs_date = new Date()
                    this.cycle.can_tag_removal = false
                } catch (ex) {
                    console.error(ex)

                    if (ex.message) {
                        this.$modalManager.add({ state: "bad", header: "Error", body: ex.message })
                    } else {
                        this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                    }
                }
            }
        },

        async confirmTaggingComplete () {
            const confirmed = confirm("This cannot be undone. Are you sure?")

            if (confirmed) {
                try {
                    const params = new URLSearchParams()
                    params.append("cycle_id", this.cycle.id)

                    await http.post("/api/managers/confirm-tagging-complete", params)

                    this.$eventManager.emit("add-toast", { message: "Thank you for completing this phase!" })

                    this.cycle.can_tag_removal = false
                } catch (ex) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                }
            }
        },

        async getFilters () {
            if (this.cycle) {
                try {
                    const { data: filters } = await http.get(`/api/cycles/${this.cycle.id}/tenant-filters`)

                    this.filters = filters.map((filter) => {
                        filter.badge = filter.count

                        return filter
                    })
                } catch (ex) {
                    if (ex.message) {
                        this.$eventManager.emit("add-toast", { message: ex.message, type: "error" })
                    } else {
                        this.$eventManager.emit("add-toast", { message: ex, type: "error" })
                    }
                }
            }
        },

        async getCoverages () {
            try {
                const { data: coverages } = await http(`/api/cycles/${this.cycle_id}/coverages`)

                this.coverages = coverages
            } catch (ex) {
                if (ex.message) {
                    this.$eventManager.emit("add-toast", { message: ex.message, type: "error" })
                } else {
                    this.$eventManager.emit("add-toast", { message: ex, type: "error" })
                }
            }
        },

        async getCycle () {
            try {
                const { data: cycle } = await http(`/api/cycles/${this.cycle_id}`)

                this.cycle = cycle
            } catch (ex) {
                if (ex.message) {
                    this.$eventManager.emit("add-toast", { message: ex.message, type: "error" })
                } else {
                    this.$eventManager.emit("add-toast", { message: ex, type: "error" })
                }
            }
        },

        async getTenants () {
            this.$refs.tenants_table.reload()
        },

        async exportData () {
            if (!this.cycle) {
                return
            }

            this.is_loading_export = true

            const params = {
                cycle_id: this.cycle.id
            }

            const filter = this.filters[this.active_filter_index]
            if (filter) {
                params.filter = filter.key
            }

            try {
                const { data: response } = await http("/api/exports/manager-tenants", { params })

                this.export_filename = response.filename
            } catch (ex) {
                console.dir(ex)
            } finally {
                this.is_loading_export = false
            }
        },

        /**
         * Set the sort field when the user requests sorting.
         *
         * @param object data Row data
         */
        manageTable (data) {
            if (data.length) {
                this.sort_field = {
                    field: data[0].sortField,
                    direction: data[0].direction
                }
            } else {
                this.sort_field = {}
            }
        },

        /**
         * Normalize the letter send dates because JavaScript automatically subtracts hours
         * for local time, making it appear as if the date selected is the day before.
         */
        normalizeDates () {
            if (!this.cycle) {
                return
            }

            if (this.cycle.initial_letter_send_date) {
                const date = new Date(this.cycle.initial_letter_send_date)
                date.setUTCHours(date.getHours())
                this.cycle.initial_letter_send_date = date
            }

            if (this.cycle.reminder_letter_send_date) {
                const date = new Date(this.cycle.reminder_letter_send_date)
                date.setUTCHours(date.getHours())
                this.cycle.reminder_letter_send_date = date
            }

            if (this.cycle.letter_effective_date) {
                const date = new Date(this.cycle.letter_effective_date)
                date.setUTCHours(date.getHours())
                this.cycle.letter_effective_date = date
            }
        },

        async onConfirmChargedAdded (tenant) {
            const message = `Are you sure you want to mark ${tenant.first_name} ${tenant.last_name} as setup?`
            const confirmed = confirm(message)

            if (confirmed) {
                this.is_loading_tenants = true

                try {
                    const params = new URLSearchParams()
                    params.append("id", tenant.id)
                    params.append("charge_applied", true)
                    params.append("cycle_id", tenant.cycle_id)

                    await http.post("/api/managers/update-tenant", params)

                    this.getFilters()
                    this.getTenants()
                } catch (ex) {
                    this.is_loading_tenants = false

                    console.dir(ex)
                }
            }
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.tenants_table_pagination.setPaginationData(paginationData)
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.tenants_table.changePage(page)
        },

        /**
         * Open the edit tenant modal.
         *
         * @param object data  Row data object
         * @param Event  ev    Event object
         */
        onRowClicked (data, ev) {
            // lock tenants that have already been sent a welcome letter
            if (data.welcome_letter_sent_date !== null) {
                return
            }

            // let columns prevent clicking from happening
            if (ev.composedPath()[0].classList.contains("no-click")) {
                return
            }

            this.edit_tenant = Object.assign({}, data)

            this.edit_tenant_modal_open = true
        },

        /**
         * Close the edit tenant modal and unload the edit tenant data.
         */
        onEditModalClose () {
            this.edit_tenant_modal_open = false
            this.edit_tenant = {}
        },

        onTableError (error) {
            const { data: { data: message } } = error.response

            this.$eventManager.emit("add-toast", { message: `Error! ${message}`, type: "error" })
        },

        onSaveTagTenants () {
            this.loadData()
        },

        /**
         * Close the tag tenants for removal modal.
         */
        onTagTenantsModalClose () {
            this.tag_tenants_modal_open = false
        },

        async onSaveTenant (confirmedChargeApplied) {
            const params = new URLSearchParams()

            this.is_saving_tenant = true
            this.error_saving_tenant_message = ""

            params.append("charge_applied", confirmedChargeApplied)

            Object.keys(this.edit_tenant).forEach(key => {
                const value = this.edit_tenant[key]

                if (this.edit_tenant[key] instanceof Date) {
                    let str
                    let month = value.getMonth() + 1
                    let day = value.getDate()
                    let hours = value.getHours()
                    let minutes = value.getMinutes()
                    let seconds = value.getSeconds()

                    if (month < 10) {
                        month = `0${month}`
                    }

                    if (day < 10) {
                        day = `0${day}`
                    }

                    if (hours < 10) {
                        hours = `0${hours}`
                    }

                    if (minutes < 10) {
                        minutes = `0${minutes}`
                    }

                    if (seconds < 10) {
                        seconds = `0${seconds}`
                    }

                    str = `${value.getFullYear()}-${month}-${day}`
                    str += ` ${hours}:${minutes}:${seconds}`

                    params.append(key, str)
                } else if (key === "units") {
                    params.append("units", this.edit_tenant[key].map(unit => unit.identifier).join(","))
                } else {
                    params.append(key, this.edit_tenant[key])
                }
            })

            try {
                await http.post("/api/managers/update-tenant", params)

                this.onEditModalClose()
                this.getFilters()
                this.getTenants()
            } catch (ex) {
                this.error_saving_tenant_message = ex
            } finally {
                this.is_saving_tenant = false
            }
        },

        /**
         * Edit the coverage field for the tenant being edited.
         *
         * @param string coverage Coverage key
         */
        onUpdateCoverage (coverage) {
            this.edit_tenant = Object.assign({}, Object.assign(this.edit_tenant, {
                coverage_id: coverage
            }))
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: { data: response } }) {
            this.last_row_count = response.data.length
            this.data_info = response.info
        },

        async onBulkSetChargeApplied () {
            const message = `This will confirm the charge has been added for ${this.checked.length} tenants. Continue?`
            const confirmed = confirm(message)

            if (confirmed) {
                this.is_loading_tenants = true

                try {
                    const params = objectToParams({ ids: this.checked.join(",") })

                    await http.post("/api/managers/bulk-charge-applied", params)

                    this.$eventManager.emit("add-toast", { message: `${this.checked.length} tenants successfully updated!` })
                    this.checked = []

                    this.$refs.tenants_table.reload()
                } catch (ex) {
                    this.is_loading_tenants = false
                }
            }
        },

        /**
         * Toggle selecting all tenants.
         *
         * @return null
         */
        onToggleAll () {
            const tenants = this.$refs.tenants_table.tableData

            if (tenants.length === this.checked.length) {
                this.checked = []
            } else {
                this.checked = tenants.map(tenant => tenant.id)
            }
        },

        /**
         * Toggle the state of a facility's checkbox.
         *
         * @param int id Facility ID
         *
         * @return null
         */
        toggleCheckbox (id) {
            if (this.checked.indexOf(id) === -1) {
                this.checked.push(id)
            } else {
                this.checked = this.checked.filter(checkId => checkId !== id)
            }
        }
    }
}
</script>

<style>
.facility-table tr + tr td {
    padding-top: 5px;
}

.facility-table td {
    vertical-align: top;
}

.facility-table td:first-child {
    text-align: right;
    padding-right: 15px;
}
</style>



// WEBPACK FOOTER //
// src/pages/manager/CycleAuditPage.vue